import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/16/solid';
import { Button } from '@leadsales/design-system/react';
import { useMessageBarContext } from '../../MessageBar.hook.ts';
import { ChatEnum } from '../../../../Chat.enum.ts';
import Template from './Components/Template/Template.component.tsx';
import { useTranslation } from 'react-i18next';
import { Loading } from 'react-daisyui';
import { useWabTemplatesAction } from 'src/components/Chat/components/MessageBar/components/WabTemplatesAction/WabTemplatesAction.hook.ts';
import './WabTemplatesAction.styles.scss';
import useWhatsappApi from 'src/pages/WhatsappApi/WhatsappApi.hook.ts';
import { useModalsContext } from 'src/providers/ModalsProvider/Modals.context.ts';
import { useTemplateCosts } from 'src/pages/Wallet/components/EstimatedCost/useTemplateCosts.hook';
import { useTemplateListPaidChip } from 'src/pages/Wallet/hooks/useTemplateListPaidChip';
import ComposerSendSeal from 'src/pages/Wallet/components/ComposerSendSeal/ComposerSendSeal.component';
import { useChatContext } from '../../../../Chat.hook.ts';
import { ChatState } from '../../../../Chat.enum.ts';

export default function WabTemplatesAction() {
  const { setSelectedAction, isSchedulePickerMode } = useMessageBarContext();
  const { t } = useTranslation(['chat/chat', 'errors/errors']);
  const {
    handleSearch,
    handleTemplateTypeFilter,
    selectedTemplateType,
    filteredTemplates,
    predefinedVariables,
    variableDefinitions,
    loading,
    hasUtilityTemplates,
    handleCreateUtilityTemplate,
  } = useWabTemplatesAction();
  const {syncTemplates,loading:loadingSyncTemplates} = useWhatsappApi()

  /*
    RET-1694 — el costo por plantilla del destinatario, antes de enviar.

    Se pide AQUÍ y no en cada renglón porque el precio depende de categoría ×
    país: con un solo destinatario el país es fijo, así que esto cotiza una vez
    por categoría distinta (≤4) en vez de una por plantilla. El teléfono es el
    mismo que el picker ya usa para pedir las plantillas y para enviar.
  */
  const { selectedLeadForChatModal } = useModalsContext();
  const formattedCostFor = useTemplateCosts({
    templateTypes: filteredTemplates.map((template) => template.template_type),
    recipientHandle: selectedLeadForChatModal?.handle,
  });

  /*
    RET-2232 — el rótulo de esa columna. Se decide con los MISMOS
    `template_type` que acaban de alimentar los precios, así que el chip y los
    renglones ven exactamente la misma lista filtrada.
  */
  const { selectedChat } = useChatContext();
  const paidChip = useTemplateListPaidChip({
    /*
      Con la lista CARGANDO no hay renglones en pantalla, y el rótulo de una
      columna vacía es peor que ningún rótulo: `showTemplates()` levanta
      `loading` SIN limpiar `filteredTemplates`, así que al cambiar de
      destinatario con el picker abierto el chip se quedaba pintado sobre el
      spinner, decidido por el catálogo del contacto ANTERIOR y con el
      tooltip ya nombrando al NUEVO. Se pasa la lista vacía y el hook, que ya
      trata «vacía» como «nada que rotular», lo apaga solo.
    */
    templateTypes: loading
      ? []
      : filteredTemplates.map((template) => template.template_type),
    recipientHandle: selectedLeadForChatModal?.handle,
    recipientName: selectedLeadForChatModal?.name,
    // Lectura ESTRICTA del estado, igual que los tres consumidores del
    // predicado (D-5): «estado desconocido» no promete gratuidad.
    isChatActive: selectedChat?.state === ChatState.ACTIVE,
    hasFreeEntryPoint: !!selectedChat?.freeEntryPointData?.hasFreeEntryPoint,
    isSchedulePickerMode,
  });

  return (
    <div className="WabTemplateActions">
      <div className="WabTemplateActions__Header">
        <p className="WabTemplateActions__Header__Title">
          {t('wabTemplateActions.title')}
        </p>
        <XMarkIcon
          onClick={() => setSelectedAction(ChatEnum.CHAT)}
          className="WabTemplateActions__Header__Icon"
        />
      </div>
      <div className="WabTemplateActions__SearchContainer">
        <div className="WabTemplateActions__SearchContainer__InputContainer">
          <div className="WabTemplateActions__SearchContainer__InputContainer__Filed">
            <MagnifyingGlassIcon className="WabTemplateActions__SearchContainer__InputContainer__Icon" />
            <input
              onChange={handleSearch}
              placeholder={t('wabTemplateActions.input.placeholder')}
              className="WabTemplateActions__SearchContainer__InputContainer__Input"
            />
          </div>
        </div>
        <div className="flex justify-end">
        <button onClick={syncTemplates} className="underline text-primary px-4">
          {loadingSyncTemplates ? <Loading /> : t('wabTemplateActions.sync-last-templates')}
          </button>
        </div>
        <div className="flex text-primary px-2 my-2">
          <button
            onClick={() => handleTemplateTypeFilter('ALL')}
            className={`border border-primary rounded-l-md px-2 py-1 ${
              selectedTemplateType === 'ALL'
                ? 'bg-primary/10 '
                : 'bg-transparent'
            }`}
          >
            {t('wabTemplateActions.filters.all')}
          </button>
          <button
            onClick={() => handleTemplateTypeFilter('UTILITY')}
            className={`border border-primary px-2 py-1 ${
              selectedTemplateType === 'UTILITY'
                ? 'bg-primary/10 '
                : 'bg-transparent'
            }`}
          >
            {t('wabTemplateActions.filters.utility')}
          </button>
          <button
            onClick={() => handleTemplateTypeFilter('MARKETING')}
            className={`border border-primary rounded-r-md px-2 py-1 ${
              selectedTemplateType === 'MARKETING'
                ? 'bg-primary/10 '
                : 'bg-transparent'
            }`}
          >
            {t('wabTemplateActions.filters.marketing')}
          </button>
          {/*
            RET-2232 — el rótulo de la columna de precios.

            Va en ESTA línea y no encima de la columna porque aquí queda
            alineado con el borde del buscador y de la propia columna (razón
            del prototipo, pantalla 10), y `ml-auto` lo empuja a la derecha.

            Es el MISMO componente del sello del compositor, no un chip
            parecido: dos piezas que dicen «Con costo» por separado es cómo
            acaban divergiendo el día del corte.
          */}
          {paidChip ? (
            <div className="ml-auto flex items-center">
              <ComposerSendSeal
                seal={{ kind: 'paid', unitCostLabel: null }}
                tooltip={paidChip.tooltip}
              />
            </div>
          ) : null}
        </div>

        {loading ? (
          <div className="WabTemplateActions__Loading">
            <Loading className="WabTemplateActions__Loading__Spinner" />
          </div>
        ) : selectedTemplateType === 'UTILITY' && !hasUtilityTemplates ? (
          // INT-1500 U2: never a blank list. The 131049 notice sends sellers
          // here, and most who hit it have no follow-up template yet.
          <div
            className="WabTemplateActions__Empty"
            data-testid="wab-templates-utility-empty"
          >
            <p className="WabTemplateActions__Empty__Title">
              {t('wabTemplateActions.utilityEmpty.title')}
            </p>
            <p className="WabTemplateActions__Empty__Body">
              {t('wabTemplateActions.utilityEmpty.body')}
            </p>
            <Button
              type="button"
              color="secondary"
              size="sm"
              onClick={handleCreateUtilityTemplate}
            >
              {t('wabTemplateActions.utilityEmpty.action')}
            </Button>
          </div>
        ) : (
          filteredTemplates.map((template) => (
            <Template key={template.template_id} template={template} predefinedVariables={predefinedVariables} variableDefinitions={variableDefinitions} formattedCost={formattedCostFor(template.template_type)} />
          ))
        )}
      </div>
    </div>
  );
}
